<template>
  <div class="card custom-shadow rounded-3 bg-white border mb-4">
    <div class="custom-padding-30">
      <h4 class="fw-semibold" v-html="title"></h4>
      <p class="mb-3 "><small v-html="t('fr.tax_breakdown.chart_description', 'Suddivisione del fatturato tra tasse e utile')"></small></p>

      <!-- Layout with Summary and Chart -->
      <div class="row" v-if="showSummary">
        <!-- Desktop: Info on left, Mobile: Hidden initially -->
        <div class="col-lg-6 order-lg-1 order-2">
          <!-- Tax Summary Info -->
          <div class="tax-summary-info d-none d-lg-block">
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="year ? t('fr.tax_summary.annual_revenue_with_year', `Fatturato Annuo ${year}`, { year }) : t('fr.tax_summary.annual_revenue', 'Fatturato Annuo')"></div>
              <div class="summary-value fw-bold">{{ formatCurrency(summaryData.fatturato) }}</div>
            </div>
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="t('fr.tax_summary.flat_rate', 'Aliquota Forfettaria')"></div>
              <div class="summary-value fw-bold">{{ summaryData.aliquota }}%</div>
            </div>
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="t('fr.tax_summary.taxable_income', 'Reddito Imponibile')"></div>
              <div class="summary-value fw-bold">{{ formatCurrency(summaryData.redditoImponibile) }}</div>
            </div>

            <!-- Campi specifici per INPS -->
            <template v-if="contributionType === 'INPS'">
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.inps_balance', 'Saldo Contributi INPS')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.inpsSaldo) }}</div>
              </div>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.inps_advances', 'Acconti Contributi INPS')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.inpsAcconti) }}</div>
              </div>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.irpef_advances', 'Acconti IRPEF')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.irpefAcconti) }}</div>
              </div>
            </template>

            <!-- Campi specifici per Casse Professionali -->
            <template v-else>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.contrib_total', 'Totale Contributi')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(chartContribSoggettivo + chartContribIntegrativo + chartContribMaternita) }}</div>
              </div>
            </template>
            <div class="summary-item mb-2">
              <div class="summary-label fw-semibold" v-html="t('fr.tax_summary.total_taxes', 'Totale da versare')"></div>
              <div class="summary-value fw-bold text-danger">{{ formatCurrency(summaryData.totaleImposte) }}</div>
            </div>

            <!-- Chart Breakdown -->
            <div class="mt-3 pt-3 border-top">
              <div class="summary-item mb-2">
                <div class="summary-label d-flex align-items-center">
                  <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #dc3545;"></span>
                  <span v-html="t('fr.tax_breakdown.legend_substitute_tax', 'Imp. Sost.')"></span>
                </div>
                <div class="summary-value fw-bold">{{ formatCurrency(chartTaxTotal) }}</div>
              </div>

              <template v-if="contributionType === 'PROFESSIONAL'">
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #fd7e14;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_soggettivo', 'Contrib. Sogg.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribSoggettivo) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #ffc107;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_integrativo', 'Contrib. Integr.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribIntegrativo) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #9c27b0;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_maternita', 'Contrib. Matern.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribMaternita) }}</div>
                </div>
              </template>

              <template v-else>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #fd7e14;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_inps_variable', 'INPS Var.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartInpsVariableTotal) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #ffc107;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_inps_fixed', 'INPS Fis.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartInpsFixedTotal) }}</div>
                </div>
              </template>

              <div class="summary-item mb-0">
                <div class="summary-label d-flex align-items-center">
                  <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #28a745;"></span>
                  <span v-html="t('fr.tax_breakdown.legend_profit', 'Utile')"></span>
                </div>
                <div class="summary-value fw-bold text-success">{{ formatCurrency(utile) }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Desktop: Chart on right, Mobile: Chart first -->
        <div class="col-lg-6 order-lg-2 order-1">
          <div style="margin: -8px 0 -13px 0">
            <apexchart
              v-if="isClient"
              type="donut"
              height="200"
              :options="chartOptions"
              :series="chartSeries"
            ></apexchart>
          </div>
          <!-- Chart Legend -->
          <div class="d-flex gap-2 justify-content-center mt-3 flex-wrap">
            <div class="d-flex align-items-center">
              <span
                class="d-inline-block rounded-circle"
                style="width: 8px; height: 8px; background-color: #dc3545;"
              ></span>
              <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_substitute_tax', 'Imp. Sost.')"></span>
            </div>

            <template v-if="contributionType === 'PROFESSIONAL'">
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block rounded-circle"
                  style="width: 8px; height: 8px; background-color: #fd7e14;"
                ></span>
                <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_contrib_soggettivo', 'Sogg.')"></span>
              </div>
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block rounded-circle"
                  style="width: 8px; height: 8px; background-color: #ffc107;"
                ></span>
                <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_contrib_integrativo', 'Integr.')"></span>
              </div>
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block rounded-circle"
                  style="width: 8px; height: 8px; background-color: #9c27b0;"
                ></span>
                <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_contrib_maternita', 'Matern.')"></span>
              </div>
            </template>

            <template v-else>
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block rounded-circle"
                  style="width: 8px; height: 8px; background-color: #fd7e14;"
                ></span>
                <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_inps_variable', 'INPS Var.')"></span>
              </div>
              <div class="d-flex align-items-center">
                <span
                  class="d-inline-block rounded-circle"
                  style="width: 8px; height: 8px; background-color: #ffc107;"
                ></span>
                <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_inps_fixed', 'INPS Fis.')"></span>
              </div>
            </template>

            <div class="d-flex align-items-center">
              <span
                class="d-inline-block rounded-circle"
                style="width: 8px; height: 8px; background-color: #28a745;"
              ></span>
              <span class="ms-1 small" v-html="t('fr.tax_breakdown.legend_profit', 'Utile')"></span>
            </div>
          </div>
        </div>

        <!-- Mobile Details Section (below chart) -->
        <div class="col-12 d-lg-none order-3">
          <!-- Mobile Toggle Button -->
          <div class="mb-2 mt-3">
            <button
              class="btn btn-outline-primary btn-sm w-100"
              type="button"
              @click="toggleDetails"
            >
              <i class="fas" :class="showDetails ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              {{ showDetails ? t('fr.tax_summary.hide_details', 'Nascondi dettagli') : t('fr.tax_summary.show_details', 'Mostra dettagli') }}
            </button>
          </div>

          <!-- Mobile Details Content -->
          <div v-show="showDetails" class="tax-summary-info">
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="year ? t('fr.tax_summary.annual_revenue_with_year', `Fatturato Annuo ${year}`, { year }) : t('fr.tax_summary.annual_revenue', 'Fatturato Annuo')"></div>
              <div class="summary-value fw-bold">{{ formatCurrency(summaryData.fatturato) }}</div>
            </div>
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="t('fr.tax_summary.flat_rate', 'Aliquota Forfettaria')"></div>
              <div class="summary-value fw-bold">{{ summaryData.aliquota }}%</div>
            </div>
            <div class="summary-item mb-2">
              <div class="summary-label" v-html="t('fr.tax_summary.taxable_income', 'Reddito Imponibile')"></div>
              <div class="summary-value fw-bold">{{ formatCurrency(summaryData.redditoImponibile) }}</div>
            </div>

            <!-- Campi specifici per INPS -->
            <template v-if="contributionType === 'INPS'">
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.inps_balance', 'Saldo Contributi INPS')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.inpsSaldo) }}</div>
              </div>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.inps_advances', 'Acconti Contributi INPS')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.inpsAcconti) }}</div>
              </div>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.irpef_advances', 'Acconti IRPEF')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(summaryData.irpefAcconti) }}</div>
              </div>
            </template>

            <!-- Campi specifici per Casse Professionali -->
            <template v-else>
              <div class="summary-item mb-2">
                <div class="summary-label" v-html="t('fr.tax_summary.contrib_total', 'Totale Contributi')"></div>
                <div class="summary-value fw-bold">{{ formatCurrency(chartContribSoggettivo + chartContribIntegrativo + chartContribMaternita) }}</div>
              </div>
            </template>
            <div class="summary-item mb-2">
              <div class="summary-label fw-semibold" v-html="t('fr.tax_summary.total_taxes', 'Totale da versare')"></div>
              <div class="summary-value fw-bold text-danger">{{ formatCurrency(summaryData.totaleImposte) }}</div>
            </div>

            <!-- Chart Breakdown -->
            <div class="mt-3 pt-3 border-top">
              <div class="summary-item mb-2">
                <div class="summary-label d-flex align-items-center">
                  <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #dc3545;"></span>
                  <span v-html="t('fr.tax_breakdown.legend_substitute_tax', 'Imp. Sost.')"></span>
                </div>
                <div class="summary-value fw-bold">{{ formatCurrency(chartTaxTotal) }}</div>
              </div>

              <template v-if="contributionType === 'PROFESSIONAL'">
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #fd7e14;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_soggettivo', 'Contrib. Sogg.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribSoggettivo) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #ffc107;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_integrativo', 'Contrib. Integr.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribIntegrativo) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #9c27b0;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_contrib_maternita', 'Contrib. Matern.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartContribMaternita) }}</div>
                </div>
              </template>

              <template v-else>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #fd7e14;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_inps_variable', 'INPS Var.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartInpsVariableTotal) }}</div>
                </div>
                <div class="summary-item mb-2">
                  <div class="summary-label d-flex align-items-center">
                    <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #ffc107;"></span>
                    <span v-html="t('fr.tax_breakdown.legend_inps_fixed', 'INPS Fis.')"></span>
                  </div>
                  <div class="summary-value fw-bold">{{ formatCurrency(chartInpsFixedTotal) }}</div>
                </div>
              </template>

              <div class="summary-item mb-0">
                <div class="summary-label d-flex align-items-center">
                  <span class="d-inline-block rounded-circle me-2" style="width: 8px; height: 8px; background-color: #28a745;"></span>
                  <span v-html="t('fr.tax_breakdown.legend_profit', 'Utile')"></span>
                </div>
                <div class="summary-value fw-bold text-success">{{ formatCurrency(utile) }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Chart only (fallback) -->
      <div v-if="!showSummary" style="margin: -8px 0 -13px 0">
        <apexchart
          v-if="isClient"
          type="donut"
          height="200"
          :options="chartOptions"
          :series="chartSeries"
        ></apexchart>
      </div>

      <!-- Calculation Details Button -->
      <div v-if="calculationDetails" class="mt-0">
        <button
          class="btn btn-outline-primary btn-sm w-100"
          type="button"
          data-bs-toggle="modal"
          :data-bs-target="'#calculationDetailsModal-' + modalId"
        >
          {{ t('fr.tax_breakdown.show_calculation_details', 'Mostra dettagli calcolo') }}
        </button>
      </div>
    </div>
  </div>

  <!-- Calculation Details Modal -->
  <div
    v-if="calculationDetails"
    class="modal fade"
    :id="'calculationDetailsModal-' + modalId"
    tabindex="-1"
    :aria-labelledby="'calculationDetailsModalLabel-' + modalId"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" :id="'calculationDetailsModalLabel-' + modalId">
            <i class="ri-calculator-line me-2"></i>
            {{ t('fr.tax_breakdown.calculation_details_title', 'Dettagli Calcolo Fiscale') }}
          </h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          ></button>
        </div>
        <div class="modal-body p-4">
          <!-- Summary -->
          <div class="mb-4">
            <div class="d-flex align-items-start">
              <i class="text-primary" style="font-size: 1.2rem; margin-top: 2px;"></i>
              <p class="mb-0" style="line-height: 1.6;">{{ calculationDetails.summary }}</p>
            </div>
          </div>

          <!-- Steps - Simple List -->
          <div class="calculation-steps-list">
            <div v-for="(step, index) in calculationDetails.steps" :key="index" class="step-item mb-3">
              <div class="step-header d-flex justify-content-between align-items-start mb-2">
                <div class="step-title">
                  <span class="fw-semibold">{{ step.title }}</span>
                </div>
                <div class="step-result text-dark fw-bold">{{ step.result }}</div>
              </div>

              <!-- Formula - Most Important -->
              <div v-if="step.formula" class="step-formula-box mb-2">
                <div class="formula-content">
                  <code class="formula-code">{{ step.formula }}</code>
                </div>
              </div>

              <!-- Description -->
              <div class="step-body">
                <p class="step-description text-muted mb-0"><small>{{ step.description }}</small></p>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer border-0 p-4 pt-0">
          <button type="button" class="btn btn-primary text-white" data-bs-dismiss="modal">
            <i class="ri-close-line me-1"></i>
            {{ t('fr.common.close', 'Chiudi') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, ref, computed, onMounted, onUnmounted } from 'vue';
import { t } from '@/utils/i18nHelper';

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  chartTaxTotal: {
    type: Number,
    required: true,
  },
  chartInpsVariableTotal: {
    type: Number,
    required: true,
  },
  chartInpsFixedTotal: {
    type: Number,
    required: true,
  },
  // Nuovi props per casse professionali
  chartContribSoggettivo: {
    type: Number,
    default: 0,
  },
  chartContribIntegrativo: {
    type: Number,
    default: 0,
  },
  chartContribMaternita: {
    type: Number,
    default: 0,
  },
  fatturato: {
    type: Number,
    required: true,
  },
  showSummary: {
    type: Boolean,
    default: false,
  },
  summaryData: {
    type: Object,
    default: () => ({}),
  },
  // Tipo di regime per determinare quali contributi mostrare
  contributionType: {
    type: String,
    default: 'INPS', // 'INPS' o 'PROFESSIONAL'
  },
  // Dettagli del calcolo dal backend
  calculationDetails: {
    type: Object as () => { summary: string; steps: Array<{ title: string; description: string; formula?: string; result: string }> } | null,
    default: null,
  },
  // Anno di riferimento del fatturato
  year: {
    type: Number,
    required: false,
  },
});

const isClient = ref(false);
const showDetails = ref(false);
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1200);

// Genera un ID univoco per il modal
const modalId = ref(`calc-${Math.random().toString(36).substring(2, 11)}`);

// Calcoliamo l'utile come differenza tra fatturato e totale tasse
const totalTaxes = computed(() => {
  if (props.contributionType === 'PROFESSIONAL') {
    return props.chartTaxTotal + props.chartContribSoggettivo + props.chartContribIntegrativo + props.chartContribMaternita;
  }
  return props.chartTaxTotal + props.chartInpsVariableTotal + props.chartInpsFixedTotal;
});

const utile = computed(() =>
  Math.max(0, props.fatturato - totalTaxes.value)
);

const chartSeries = computed(() => {
  if (props.contributionType === 'PROFESSIONAL') {
    return [
      props.chartTaxTotal,
      props.chartContribSoggettivo,
      props.chartContribIntegrativo,
      props.chartContribMaternita,
      utile.value,
    ];
  }
  return [
    props.chartTaxTotal,
    props.chartInpsVariableTotal,
    props.chartInpsFixedTotal,
    utile.value,
  ];
});

// Uso diretto della funzione t() nelle computed

const chartOptions = computed(() => {
  let labels, colors;

  if (props.contributionType === 'PROFESSIONAL') {
    labels = [
      t('fr.tax_breakdown.chart_label_substitute_tax', 'Imposta Sostitutiva'),
      t('fr.tax_breakdown.chart_label_contrib_soggettivo', 'Contrib. Soggettivo'),
      t('fr.tax_breakdown.chart_label_contrib_integrativo', 'Contrib. Integrativo'),
      t('fr.tax_breakdown.chart_label_contrib_maternita', 'Contrib. Maternità'),
      t('fr.tax_breakdown.chart_label_profit', 'Utile')
    ];
    colors = ["#dc3545", "#fd7e14", "#ffc107", "#9c27b0", "#28a745"]; // Rosso, Arancione, Giallo, Viola per maternità, Verde
  } else {
    labels = [
      t('fr.tax_breakdown.chart_label_substitute_tax', 'Imposta Sostitutiva'),
      t('fr.tax_breakdown.chart_label_inps_variable', 'Contributi INPS Variabili'),
      t('fr.tax_breakdown.chart_label_inps_fixed', 'Contributi INPS Fissi'),
      t('fr.tax_breakdown.chart_label_profit', 'Utile')
    ];
    colors = ["#dc3545", "#fd7e14", "#ffc107", "#28a745"]; // Rosso per tasse, Arancione per INPS Var, Giallo per INPS Fissi, Verde per utile
  }

  // Cattura il valore del fatturato per usarlo nel formatter
  const fatturatoValue = props.fatturato;

  return {
  chart: {
    height: 200,
    type: "donut",
  },
  labels,
  colors,
  stroke: {
    width: 1,
    show: true,
    colors: ["#ffffff"],
    lineCap: "round",
  },
  legend: {
    show: false,
  },
  plotOptions: {
    pie: {
      expandOnClick: false,
      donut: {
        size: "70%",
        labels: {
          show: true,
          name: {
            color: "#64748B",
          },
          value: {
            show: true,
            color: "#64748B",
            fontSize: "18px",
            fontWeight: "600",
            offsetY: 1,
            formatter: function(val: any) {
              const numVal = parseFloat(val);
              if (!isFinite(numVal)) return '€ 0,00'; // Use isFinite for a more robust check
              const formatted = numVal.toFixed(2);
              const parts = formatted.split('.');
              const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
              const decimalPart = parts[1];
              return '€ ' + integerPart + ',' + decimalPart;
            }
          },
          total: {
            show: true,
            label: props.year ? t('fr.tax_breakdown.chart_total_label_with_year', `Fatturato ${props.year}`, { year: props.year }) : t('fr.tax_breakdown.chart_total_label', 'Totale'),
            color: "#64748B",
            fontSize: "12px",
            fontWeight: "400",
            formatter: function(w: any) {
              // Mostra sempre il fatturato originale, non la somma delle serie
              const total = fatturatoValue;
              const formatted = total.toFixed(2);
              const parts = formatted.split('.');
              const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
              const decimalPart = parts[1];
              return '€ ' + integerPart + ',' + decimalPart;
            }
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  tooltip: {
    enabled: true,
    custom: function({ series, seriesIndex, w }: any) {
      const value = series[seriesIndex];
      const label = w.globals.labels[seriesIndex];
      const numVal = parseFloat(value);
      let formattedValue;
      if (isNaN(numVal)) {
        formattedValue = '€ 0,00';
      } else {
        const formatted = numVal.toFixed(2);
        const parts = formatted.split('.');
        const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        const decimalPart = parts[1];
        formattedValue = '€ ' + integerPart + ',' + decimalPart;
      }
      return '<div class="custom-tooltip" style="padding: 8px 12px; background: #000; color: #fff; border-radius: 4px; font-size: 12px;">' +
             '<strong>' + label + '</strong><br/>' +
             formattedValue +
             '</div>';
    }
  },
  };
});

// Funzione per formattare la valuta
const formatCurrency = (value: number) => {
  if (value === null || value === undefined || isNaN(value)) {
    return '€ 0,00';
  }

  const formatted = Math.abs(value).toFixed(2);
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1];

  const sign = value < 0 ? '-' : '';
  return sign + '€ ' + integerPart + ',' + decimalPart;
};

// Funzione per toggle dei dettagli
const toggleDetails = () => {
  showDetails.value = !showDetails.value;
};

// Funzione per gestire il resize della finestra
const handleResize = () => {
  windowWidth.value = window.innerWidth;
};

onMounted(() => {
  isClient.value = true;

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize);
    windowWidth.value = window.innerWidth;
  }
});

onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize);
  }
});
</script>

<style scoped>
/* Tax Summary Info Styles */
.tax-summary-info {
  font-size: 0.85rem;
  padding: 10px 0;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.summary-label {
  color: #64748B;
  font-size: 0.8rem;
  line-height: 1.2;
  flex: 1;
}

.summary-value {
  color: #1e293b;
  font-size: 0.85rem;
  text-align: right;
  white-space: nowrap;
  flex-shrink: 0;
}

/* Removed - uniform font size for all summary values */

/* Mobile toggle button */
.btn-outline-primary {
  border-color: #6c757d;
  color: #6c757d;
  font-size: 0.8rem;
}

.btn-outline-primary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
}

.btn-outline-primary i {
  font-size: 0.7rem;
  margin-right: 5px;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .summary-item {
    flex-direction: column;
    gap: 1px;
  }

  .summary-value {
    text-align: left;
  }

  .tax-summary-info {
    margin-bottom: 15px;
  }
}

@media (max-width: 991px) {
  .tax-summary-info {
    margin-bottom: 20px;
  }
}

/* Calculation Details Styles */
.calculation-details {
  font-size: 0.85rem;
}

.calculation-details .alert-info {
  background-color: #e7f3ff;
  border-color: #b3d9ff;
  color: #004085;
  font-size: 0.85rem;
}

.step-item {
  padding: 1rem;
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-left: 4px solid #0d6efd;
  border-radius: 0.375rem;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.step-item:hover {
  border-left-color: #0b5ed7;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.step-header {
  gap: 1rem;
}

.step-title {
  flex: 1;
  font-size: 0.95rem;
  color: #212529;
}

.step-number {
  color: #0d6efd;
  font-size: 1rem;
}

.step-result {
  font-size: 1rem;
  white-space: nowrap;
  flex-shrink: 0;
  color: #0d6efd;
}

/* Formula Box - Highlighted and Prominent */
.step-formula-box {
  background: rgb(231, 231, 231);
  border: 1px solid gray;
  border-radius: 0.375rem;
  padding: 0.25rem 0.35rem;
}

.formula-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.formula-code {
  font-family: 'Courier New', Courier, monospace;
  font-weight: 500;
  color: gray;
  background-color: transparent;
  padding: 0;
  border: none;
  display: block;
  width: 100%;
  line-height: 1.6;
}

.step-body {

}

.step-description {
  font-size: 0.85rem;
  line-height: 1.5;
  color: #6c757d;
}

.btn-outline-secondary {
  font-size: 0.85rem;
  padding: 0.5rem 1rem;
}

.btn-outline-secondary i {
  font-size: 0.75rem;
  margin-right: 0.5rem;
}
</style>